import api from './iso20022Api';

/**
 * The cross-border corridor's calls, on the ISO 20022 engine (proxied at
 * /iso20022/api) with the same session as the ISO 20022 corridor. Every scheme
 * profile has its own base path: /api/switch/iso20022/xb/{profileId}.
 */
const XB = '/switch/iso20022/xb';
const base = (profileId) => `${XB}/${encodeURIComponent(profileId)}`;

/** An XML exchange answered as it came: status, headers and body text, whatever the status. */
async function exchange(method, url, xml, headers = {}) {
  const r = await api.request({
    method, url, data: xml,
    headers: xml === undefined ? headers : { 'Content-Type': 'application/xml', ...headers },
    responseType: 'text',
    transformResponse: (d) => d,
    validateStatus: () => true,
  });
  return { status: r.status, headers: r.headers || {}, body: typeof r.data === 'string' ? r.data : '' };
}

export const xbApi = {
  /** The schemes this participant is assigned, each with its profile summarised and any problem. */
  mySchemes: async () => (await api.get(XB)).data,

  /** Regional schemes the Sandbox has no profile for yet, with basic public facts about each. */
  comingSoon: async () => (await api.get(`${XB}/coming-soon`)).data,

  /** One profile in full: { summary, assignment, profile, incoming, endpoint }. */
  profile: async (profileId) => (await api.get(`${base(profileId)}/profile`)).data,

  /** The profile's synthetic watch-list as a CSV blob, with its file name. */
  watchlist: async (profileId) => {
    const r = await api.get(`${base(profileId)}/watchlist.csv`, { responseType: 'blob' });
    const m = (r.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/);
    return { blob: r.data, filename: m ? m[1] : `${profileId}-watchlist.csv` };
  },

  // ── Incoming payments ────────────────────────────────────────────────────
  /** The latest incoming cases, newest first, their deadlines checked. */
  incoming: async (profileId) => (await api.get(`${base(profileId)}/incoming`)).data,
  incomingCase: async (profileId, id) => (await api.get(`${base(profileId)}/incoming/${id}`)).data,
  /** Has the Sandbox send a payment for the scenario: { scenario, creditorAccount?, creditorName?, amount? }. */
  triggerIncoming: async (profileId, request) =>
    (await api.post(`${base(profileId)}/incoming/trigger`, request)).data,
  /** Posts a signed pacs.002 answering an incoming payment; { incoming, note }. */
  answerIncoming: async (profileId, signedXml) =>
    (await api.post(`${base(profileId)}/incoming/status`, signedXml,
      { headers: { 'Content-Type': 'application/xml' } })).data,

  // ── FX quotes ────────────────────────────────────────────────────────────
  /** { to, basis: FIXED_SEND | FIXED_RECEIVE, amount, uetr? }; an optional X-Cert-Scenario steers it. */
  createQuote: async (profileId, request, scenario) =>
    (await api.post(`${base(profileId)}/fx/quotes`, request,
      { headers: scenario ? { 'X-Cert-Scenario': scenario } : {} })).data,
  acceptQuote: async (profileId, quoteId) =>
    (await api.post(`${base(profileId)}/fx/quotes/${encodeURIComponent(quoteId)}/accept`)).data,
  quote: async (profileId, quoteId) =>
    (await api.get(`${base(profileId)}/fx/quotes/${encodeURIComponent(quoteId)}`)).data,

  // ── Trying messages ──────────────────────────────────────────────────────
  /**
   * Signs a message as this participant, with its key in the Sandbox: in the
   * profile's own wire format where it has one (PAPSS: the whole Message,
   * enveloped), else the domestic switch's business-message signature.
   */
  sign: (profileId, xml, montran) => montran
    ? exchange('post', `${base(profileId)}/papss/sign`, xml)
    : exchange('post', '/signWithoutSaving', xml),
  /** Sends a signed pacs.008 to the scheme. */
  sendCredit: (profileId, signedXml, polled, scenario) => exchange('post',
    polled ? `${base(profileId)}/papss/Message` : `${base(profileId)}/clearing/credit`, signedXml,
    scenario ? { 'X-Cert-Scenario': scenario } : {}),
  /** Sends a signed pacs.028 asking after a payment. */
  sendStatusRequest: (profileId, signedXml, polled) => exchange('post',
    polled ? `${base(profileId)}/papss/Message` : `${base(profileId)}/clearing/status-request`, signedXml),
  /** Sends a signed pacs.002 answering an incoming payment, through the scheme's own endpoint where it polls. */
  sendAnswer: (profileId, signedXml, polled) => exchange('post',
    polled ? `${base(profileId)}/papss/Message` : `${base(profileId)}/incoming/status`, signedXml),
  /** Takes the next message from the inbox (a polling scheme), as the participant's own system would. */
  poll: (profileId) => exchange('get', `${base(profileId)}/papss/Message`),
  /** Confirms a polled message by its sequence number: Stored or NotFound. */
  ack: (profileId, seq) => exchange('post', `${base(profileId)}/papss/MessageAck`, undefined,
    { 'X-PAPSSRTP-MessageSeq': String(seq) }),
};

export default xbApi;
